// library imports
import { useEffect, useState, useContext } from "react";
// hook imports
import useFetch from "../../../hooks/useFetch";
// context imports
import { IndexContext } from "../../../context/IndexContext";
// config imports
import { MQTT_REFRESH_RATE } from "../../../configs/configs";
// component import
import GensetContent from "./GensetContent";
import DeviceInformation from "../common/DeviceInformation";

export default function Genset({
  devicePhaseNumberList, // phase number list
  card, // state to show table or card view for data
  handleChange, // to set table or card view for data
  openModal, // state to show modal for device information
  setOpenModal, // to set modal open / close for device information
}) {
  const [fetchWrapper] = useFetch();
  const { siteSelected, notificationApi } = useContext(IndexContext);

  // phase number selected for genset system
  const [phaseNumberSelected, setPhaseNumberSelected] = useState(null);

  // device list for genset system
  const [deviceList, setDeviceList] = useState([]);

  // currently selected device ID (to view the table / card data)
  const [selectedDeviceID, setSelectedDeviceID] = useState();

  // loading states
  const [systemLoading, setSystemLoading] = useState(true);
  const [deviceLoading, setDeviceLoading] = useState(true);
  const [deviceSelectionLoading, setDeviceSelectionLoading] = useState(false);

  // table, card and system data
  const [tableData, setTableData] = useState([]);
  const [cardData, setCardData] = useState([]);
  const [systemData, setSystemData] = useState({});

  // last update date and time
  const [systemLastUpdate, setSystemLastUpdate] = useState("-");
  const [deviceLastUpdate, setDeviceLastUpdate] = useState("-");

  /** API to get device name for the device selection */
  function getDeviceName() {
    setDeviceSelectionLoading(true);
    fetchWrapper({
      endpoint_url: "system/getDeviceName",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        phase_number: phaseNumberSelected,
      },
      onSuccess: (response) => {
        setDeviceList(response.data?.GENSET ?? []);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get device name for genset system page.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setDeviceSelectionLoading(false);
      },
    });
  }

  /** API to get genset device data */
  function getDeviceData() {
    fetchWrapper({
      endpoint_url: "system/getDeviceData",
      method: "GET",
      set_content_type_header: false,
      params: {
        device_id: selectedDeviceID ? selectedDeviceID : 0,
      },

      onSuccess: (response) => {
        setTableData(response.data.table);
        setCardData(response.data.card);
        setDeviceLastUpdate(response.data.last_update);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get genset device data.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setDeviceLoading(false);
      },
    });
  }

  /** API to get genset system data */
  function getSystemData() {
    fetchWrapper({
      endpoint_url: "system/getSystemGenset",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSystemData(response.data.system_data);
        setSystemLastUpdate(response.data.last_update);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get genset system data.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setSystemLoading(false);
      },
    });
  }

  // use effect to set phase number selected
  useEffect(() => {
    if (devicePhaseNumberList && devicePhaseNumberList.length > 0) {
      setPhaseNumberSelected(devicePhaseNumberList[0].key);
    }
  }, [devicePhaseNumberList]);

  // use effect to get device name when phase number selected and site changes
  useEffect(() => {
    if (phaseNumberSelected && siteSelected) {
      getDeviceName();
    }
  }, [phaseNumberSelected, siteSelected]);

  // use effect to get system data
  // and set card, table and system data to empty list when site changes
  useEffect(() => {
    setCardData([]);
    setTableData([]);
    setSystemData({});

    if (siteSelected) {
      getSystemData();

      const timer = setInterval(() => {
        getSystemData();
      }, MQTT_REFRESH_RATE * 1000);
      return () => {
        clearInterval(timer);
      };
    }
  }, [siteSelected]);

  // use effect to get device data
  useEffect(() => {
    const timer = setInterval(() => {
      getDeviceData();
    }, MQTT_REFRESH_RATE * 1000);
    return () => {
      clearInterval(timer);
    };
  }, [selectedDeviceID]);

  // use effect to set selected device id if device list is not empty
  // else set table and card data to empty list
  useEffect(() => {
    if (deviceList && deviceList.length > 0) {
      setSelectedDeviceID(deviceList[0].value);
    } else {
      setTableData([]);
      setCardData([]);
    }
  }, [deviceList]);

  return (
    <>
      <GensetContent
        devicePhaseNumberList={devicePhaseNumberList}
        deviceList={deviceList}
        card={card}
        handleChange={handleChange}
        setOpenModal={setOpenModal}
        handleDeviceChange={(key) => {
          setSelectedDeviceID(key);
          setDeviceLoading(true);
        }}
        systemLoading={systemLoading}
        deviceLoading={deviceLoading}
        deviceSelectionLoading={deviceSelectionLoading}
        selectedDeviceID={selectedDeviceID}
        systemData={systemData}
        cardData={cardData}
        tableData={tableData}
        systemLastUpdate={systemLastUpdate}
        deviceLastUpdate={deviceLastUpdate}
        setPhaseNumberSelected={setPhaseNumberSelected}
      />
      <DeviceInformation
        deviceCardData={cardData}
        openModal={openModal}
        setOpenModal={setOpenModal}
        title="Genset System Card"
      />
    </>
  );
}
